import React from 'react';
import { Modal, Button, Typography } from 'antd';
import { Coins } from 'lucide-react';
import { getLoggedInUser, getSelectedAgent } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';

interface InsufficientCreditsModalProps {
	isOpen: boolean;
	onClose: () => void;
	isDemo: boolean;
	onStartTrial: () => void;
	onManageCredits: () => void;
}

const InsufficientCreditsModal: React.FC<InsufficientCreditsModalProps> = ({
	isOpen,
	onClose,
	isDemo,
	onStartTrial,
	onManageCredits,
}) => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgent = useAppSelector(getSelectedAgent);

	const isNotResellerRebilledClient =
		loggedInUser.resellerId &&
		((selectedAgent?.type === 'Inbound' &&
			(!loggedInUser.inboundSubscriptionId ||
				loggedInUser.inboundSubscriptionStatus === 'canceled')) ||
			(selectedAgent?.type === 'Outbound' &&
				(!loggedInUser?.outboundSubscriptionId ||
					loggedInUser.outboundSubscriptionStatus === 'canceled')));
	const isResellerClientWithProduct =
		loggedInUser.resellerId &&
		((selectedAgent?.type === 'Inbound' &&
			loggedInUser.inboundSubscriptionId &&
			loggedInUser.inboundSubscriptionStatus !== 'canceled') ||
			(selectedAgent?.type === 'Outbound' &&
				loggedInUser?.outboundSubscriptionId &&
				loggedInUser.outboundSubscriptionStatus !== 'canceled'));

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			footer={null}
			width={400}
			className="rounded-lg overflow-hidden"
		>
			<div className="p-6 text-center">
				<div className="mb-4">
					<div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
						<Coins className="w-8 h-8 text-red-500" />
					</div>
					<Typography.Title level={4} className="!mb-2">
						{isNotResellerRebilledClient ? 'Access Restricted' : 'Insufficient Credits'}
					</Typography.Title>
				</div>

				{isNotResellerRebilledClient ? (
					<>
						<div className="mb-6">
							<p className="text-gray-500">
								We are unable to process your request to make the call. Please contact your
								administrator for assistance.
							</p>
						</div>
						<Button type="primary" onClick={onClose} className="w-full h-10">
							Close
						</Button>
					</>
				) : isResellerClientWithProduct ? (
					<>
						<div className="mb-6">
							<p className="text-gray-500">
								You don't have enough credits to make test calls. Please add more credits to
								continue.
							</p>
							<p className="text-gray-500 mt-2">
								If you have sufficient credits and still facing this issue, please contact your
								administrator.
							</p>
						</div>
						<Button type="primary" onClick={onManageCredits} className="w-full h-10">
							Manage Credits
						</Button>
					</>
				) : isDemo ? (
					<>
						<div className="space-y-4 mb-6">
							<div className="bg-gray-50 p-4 rounded-lg">
								<p className="text-gray-600 mb-2">Demo Credits</p>
								<p className="text-2xl font-semibold text-gray-800">0/50</p>
							</div>
							<p className="text-gray-500">
								You've used all your demo credits. Start your free trial to make test calls and
								unlock all premium features.
							</p>
						</div>
						<Button type="primary" onClick={onStartTrial} className="w-full h-10">
							Start 7-Day Trial
						</Button>
					</>
				) : (
					<>
						<div className="mb-6">
							<p className="text-gray-500">
								You don't have enough credits to make test calls. Please add more credits to
								continue.
							</p>
						</div>
						<Button type="primary" onClick={onManageCredits} className="w-full h-10">
							Manage Credits
						</Button>
					</>
				)}
			</div>
		</Modal>
	);
};

export default InsufficientCreditsModal;
